import {
  Drawer as ChakraDrawer,
  DrawerFooterProps,
  DrawerHeaderProps,
  DrawerRootProps,
  Flex,
  FlexProps,
  Portal,
  PortalProps,
} from '@chakra-ui/react'
import { ReactNode, useEffect, useState } from 'react'
import { forceDark, forceLight, sleep, useIntercomUtils, useScrolling } from 'utils'
import { IconButton, Text, XMarkIcon } from '.'
import { ColorMode } from './providers'

export interface Props extends DrawerRootProps {
  bodyProps?: FlexProps
  /** default light */
  colorMode?: ColorMode | 'auto'
  footer?: ReactNode
  footerProps?: DrawerFooterProps
  header?: ReactNode
  headerActions?: ReactNode
  headerIcon?: JSX.Element
  headerProps?: DrawerHeaderProps
  hideClose?: boolean
  onClose?: () => void
  portalProps?: PortalProps
}

export default function Drawer({
  bodyProps,
  colorMode = 'light',
  children,
  footer,
  footerProps,
  header,
  headerActions,
  headerIcon,
  headerProps,
  hideClose,
  onClose,
  onOpenChange,
  portalProps,
  ...rest
}: Props) {
  const { scrolling, overflowRef, contentRef } = useScrolling()
  useIntercomUtils(rest.open)
  const [fullyOpen, setFullyOpen] = useState(false)

  useEffect(() => {
    if (rest.open) sleep(500).then(() => setFullyOpen(true))
  }, [rest.open])

  return (
    <ChakraDrawer.Root
      onOpenChange={details => {
        if (!details.open) {
          setFullyOpen(false)
          sleep().then(() => onClose?.())
        }
        onOpenChange?.(details)
      }}
      size="sm"
      trapFocus={false}
      {...rest}
    >
      <Portal {...portalProps}>
        <ChakraDrawer.Backdrop />
        <ChakraDrawer.Positioner padding={2}>
          <ChakraDrawer.Content
            borderRadius="md"
            transform={fullyOpen ? 'none !important' : undefined}
            {...(colorMode === 'auto' ? undefined : colorMode === 'dark' ? forceDark : forceLight)}
            {...(colorMode === 'light' && {
              bgGradient: 'to-r',
              gradientTo: '#F9FBFC',
              gradientFrom: '#F1F3FB',
            })}
          >
            {header && (
              <ChakraDrawer.Header
                alignItems="center"
                pl={5}
                pr={hideClose ? 5 : '50px'}
                py={0}
                minHeight="59px"
                flexShrink={0}
                textStyle="bodyMedium"
                borderBottomWidth={scrolling ? '1px' : 'none'}
                borderColor="rgba(222, 226, 244, 1)"
                {...headerProps}
              >
                <Flex align="center" gap={2} flex={1}>
                  {headerIcon}
                  {typeof header === 'string' ? (
                    <Text textStyle="bodyMedium">{header}</Text>
                  ) : (
                    header
                  )}
                </Flex>
                {headerActions}
              </ChakraDrawer.Header>
            )}
            {!hideClose && (
              <ChakraDrawer.CloseTrigger asChild top="8px">
                <IconButton aria-label="close drawer" icon={<XMarkIcon />} />
              </ChakraDrawer.CloseTrigger>
            )}

            <ChakraDrawer.Body p={0} ref={overflowRef}>
              <Flex ref={contentRef} flex={1} flexDirection="column" px={5} py={2} {...bodyProps}>
                {children}
              </Flex>
            </ChakraDrawer.Body>

            {footer && (
              <ChakraDrawer.Footer
                borderTopWidth={scrolling ? '1px' : 'none'}
                borderColor="rgba(222, 226, 244, 1)"
                p={2}
                {...footerProps}
              >
                {footer}
              </ChakraDrawer.Footer>
            )}
          </ChakraDrawer.Content>
        </ChakraDrawer.Positioner>
      </Portal>
    </ChakraDrawer.Root>
  )
}
